/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';

import { FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints, typography2 } from '@coursera/cds-core';

const DEFAULT_COUNTRY_CODE = 'US';

const CURRENCY_CODES: { [key: string]: string } = {
  US: 'USD',
  IN: 'INR',
};

type Props = {
  roleTitle: string;
  salary?: number | null;
  width: number;
  footnoteReference: string;
  variant?: 'base';
  countryCode?: string;
};

const grow = (width: number) => keyframes`
  from, 0%, to {
    width: 0%;
    opacity: 0;
  }

  100% {
    width: ${width}%;
    opacity: 1;
  }
`;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    ${breakpoints.up('sm')} {
      flex-direction: row;
      gap: var(--cds-spacing-200);
      align-items: center;
      opacity: 0;
      animation: 0.5s ease-in-out 1s 1 normal forwards running ${grow(100)};
    }

    & + & {
      padding-top: var(--cds-spacing-150);
    }
  `,
  roleTitleContainer: (width: number, isBase: boolean) => css`
    ${breakpoints.up('sm')} {
      background: ${isBase ? 'var(--cds-color-neutral-disabled)' : 'var(--cds-color-interactive-primary-pressed)'};
      padding: 6px 8px;
      border-radius: 4px;
      width: ${width}%;
    }
  `,
  roleTitle: (isBase: boolean) => css`
    font-weight: 400;
    white-space: nowrap;
    ${breakpoints.up('sm')} {
      font-weight: 600;
      color: ${isBase ? 'var(--cds-color-neutral-primary-weak)' : 'var(--cds-color-neutral-primary-invert)'};
    }
  `,
  salaryContainer: (width: number, isBase: boolean) => css`
    background: ${isBase ? 'var(--cds-color-neutral-disabled)' : 'var(--cds-color-interactive-primary-pressed)'};
    width: ${width}%;
    padding: var(--cds-spacing-50) var(--cds-spacing-100);
    border-radius: 4px;
    ${breakpoints.up('sm')} {
      font-weight: 600;
      width: auto;
      color: ${isBase ? 'var(--cds-color-neutral-primary-weak)' : 'var(--cds-color-neutral-primary-invert)'};
      background: transparent;
      border-radius: unset;
      padding: var(--cds-spacing-150) var(--cds-spacing-200);
    }
  `,
  salary: (isBase: boolean) => css`
    color: ${isBase ? 'var(--cds-color-neutral-primary-weak)' : 'var(--cds-color-neutral-primary-invert)'};
    font-weight: 600;
    ${breakpoints.up('sm')} {
      color: var(--cds-color-neutral-primary-weak);
      ${typography2.bodySecondary}
      font-weight: 600;
    }
  `,
};

const SalaryBar = ({
  roleTitle,
  salary,
  footnoteReference,
  width,
  variant,
  countryCode = DEFAULT_COUNTRY_CODE,
}: Props) => {
  if (!roleTitle || !salary) {
    return null;
  }

  const isBase = variant === 'base';

  return (
    <div css={styles.container}>
      <div css={styles.roleTitleContainer(width, isBase)}>
        <Typography2 variant="bodySecondary" component="div" css={styles.roleTitle(isBase)}>
          {roleTitle}
        </Typography2>
      </div>
      <div css={styles.salaryContainer(width, isBase)}>
        <Typography2 variant="bodyPrimary" component="div" css={styles.salary(isBase)}>
          <FormattedNumber
            value={salary}
            currency={CURRENCY_CODES[countryCode] ?? CURRENCY_CODES[DEFAULT_COUNTRY_CODE]}
            // eslint-disable-next-line react/style-prop-object
            style="currency"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
          />
          <sup>{footnoteReference}</sup>
        </Typography2>
      </div>
    </div>
  );
};

export default SalaryBar;
